// frontend/src/pages/StoreDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

/* ---------- helpers ---------- */
const fmtDateTime = (s) => {
  if (!s) return "—";
  let d = new Date(s);
  if (isNaN(d.getTime())) {
    const cleaned = String(s).replace("Z", "").split(".")[0];
    d = new Date(cleaned);
  }
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseTs = (s) => {
  if (!s) return 0;
  let d = new Date(s);
  if (isNaN(d.getTime())) {
    const cleaned = String(s).replace("Z", "").split(".")[0];
    d = new Date(cleaned);
  }
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

const statusBadgeClass = (st) => {
  const s = (st || "").toUpperCase();
  if (s === "SUBMITTED" || s === "INVIATO") return "badge bg-info text-dark";
  if (s === "APPROVED") return "badge bg-primary";
  if (s === "FULFILLED") return "badge bg-success";
  if (s === "CANCELED") return "badge bg-secondary";
  return "badge bg-secondary";
};
/* -------------------------------- */

function SortHeader({ label, colKey, sort, onSort }) {
  const dir = sort.key === colKey ? sort.dir : null;
  return (
    <button
      className="btn btn-link p-0 text-decoration-none"
      onClick={() => onSort(colKey)}
      title="Ordina"
    >
      {label} {dir === "asc" ? "▲" : dir === "desc" ? "▼" : ""}
    </button>
  );
}

function SectionTable({ title, rows, type, onDetail, onOpenOrder, sort, setSort, q }) {
  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    let arr = [...rows];
    if (k) {
      arr = arr.filter((r) =>
        [r.code, r.name]
          .map((v) => (v || "").toLowerCase())
          .some((v) => v.includes(k))
      );
    }

    const key = sort.key;
    const dir = sort.dir;

    const getSortVal = (row, key) => {
      if (key === "last_order") {
        return parseTs(row?.last_order?.date);
      }
      return row[key] ?? "";
    };

    if (key) {
      arr.sort((a, b) => {
        const va = getSortVal(a, key);
        const vb = getSortVal(b, key);
        const bothNum = typeof va === "number" && typeof vb === "number";
        if (bothNum) return dir === "asc" ? va - vb : vb - va;
        const sa = String(va).toLowerCase();
        const sb = String(vb).toLowerCase();
        return dir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
      });
    }
    return arr;
  }, [rows, sort, q]);

  const th = (label, key) => (
    <th>
      <SortHeader
        label={label}
        colKey={key}
        sort={sort}
        onSort={(k) => {
          setSort((s) =>
            s.key !== k ? { key: k, dir: "desc" } : { key: k, dir: s.dir === "desc" ? "asc" : "desc" }
          );
        }}
      />
    </th>
  );

  return (
    <>
      <h6 className="mb-2">{title}</h6>
      <div className="table-responsive">
        <table className="table align-middle">
          <thead>
            <tr>
              <th>Codice</th>
              <th>Nome</th>
              {th("# Ordini", "orders_count")}
              {th("Totale ordini", "revenue_total")}{/* label aggiornato */}
              {th("Insoluti", "unpaid_total")}
              {th("Provvigioni", "commission_total")}
              {th("Ultimo", "last_order")}
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-muted">
                  Nessun elemento.
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const warn = Number(r.unpaid_total || 0) > 0;
                const lastNode = r.last_order ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => onOpenOrder(r.last_order.id)}
                      title="Apri ordine"
                    >
                      #{r.last_order.id}
                    </button>{" "}
                    — {fmtDateTime(r.last_order.date)}{" "}
                    <span className={`${statusBadgeClass(r.last_order.status)} ms-1`}>
                      {(r.last_order.status || "").toUpperCase()}
                    </span>
                  </>
                ) : (
                  "—"
                );

                return (
                  <tr key={`${type}-${r.id}`} className={warn ? "table-warning" : ""}>
                    <td>{r.code || "—"}</td>
                    <td>{r.name}</td>
                    <td>{r.orders_count}</td>
                    <td>€ {Number(r.revenue_total || 0).toFixed(2)}</td>
                    <td className={Number(r.unpaid_total || 0) > 0 ? "text-danger" : ""}>
                      € {Number(r.unpaid_total || 0).toFixed(2)}
                    </td>
                    <td>€ {Number(r.commission_total || 0).toFixed(2)}</td>
                    <td>{lastNode}</td>
                    <td className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onDetail(type, r.id)}
                      >
                        Dettagli
                      </button>
                      {r.last_order?.id && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => onOpenOrder(r.last_order.id)}
                          title="Apri l’ordine più recente"
                        >
                          Apri ordine
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function StoreDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState({ franchising: [], aziende: [], pv: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const [sortF, setSortF] = useState({ key: "unpaid_total", dir: "desc" });
  const [sortA, setSortA] = useState({ key: "unpaid_total", dir: "desc" });
  const [sortP, setSortP] = useState({ key: "unpaid_total", dir: "desc" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/kpi/stores/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Errore");
      setData({
        franchising: json.franchising || [],
        aziende: json.aziende || [],
        pv: json.pv || [],
      });
    } catch (e) {
      setData({ franchising: [], aziende: [], pv: [] });
      alert(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toDetail = (type, id) => nav(`/dashboard-store/${type.toUpperCase()}/${id}`);
  const openOrder = (orderId) => nav(`/ordini/${orderId}`);

  const exportCsv = () => {
    const typeLabel = { franchising: "FRANCHISING", aziende: "AZIENDA", pv: "PUNTO VENDITA" };
    const rows = [
      ["Tipo", "Codice", "Nome", "Ordini", "Totale ordini", "Insoluti", "Provvigioni", "Ultimo (id-data-stato)"], // header aggiornato
      ...["franchising", "aziende", "pv"].flatMap((t) =>
        (data[t] || []).map((r) => [
          typeLabel[t] || t.toUpperCase(),
          r.code || "",
          r.name || "",
          r.orders_count || 0,
          Number(r.revenue_total || 0).toFixed(2), // valore invariato
          Number(r.unpaid_total || 0).toFixed(2),
          Number(r.commission_total || 0).toFixed(2),
          r.last_order
            ? `#${r.last_order.id} ${fmtDateTime(r.last_order.date)} ${(r.last_order.status || "").toUpperCase()}`
            : "",
        ])
      ),
    ];
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "stores_summary.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">🏙️ Dashboard Store</h2>
        <div className="d-flex gap-2">
          <input
            className="form-control"
            style={{ minWidth: 260 }}
            placeholder="🔎 Cerca per nome/codice…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="btn btn-outline-primary" onClick={fetchData}>
            🔁 Aggiorna
          </button>
          <button className="btn btn-outline-secondary" onClick={exportCsv}>
            ⤓ CSV
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : (
        <>
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <SectionTable
                title="Franchising"
                rows={data.franchising}
                type="FRANCHISING"
                onDetail={toDetail}
                onOpenOrder={openOrder}
                sort={sortF}
                setSort={setSortF}
                q={q}
              />
            </div>
          </div>

          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <SectionTable
                title="Aziende"
                rows={data.aziende}
                type="AZIENDA"
                onDetail={toDetail}
                onOpenOrder={openOrder}
                sort={sortA}
                setSort={setSortA}
                q={q}
              />
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <SectionTable
                title="Punti Vendita"
                rows={data.pv}
                type="PV"
                onDetail={toDetail}
                onOpenOrder={openOrder}
                sort={sortP}
                setSort={setSortP}
                q={q}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
